import uuid from 'uuid';
import { KEYUTIL, KJUR } from 'jsrsasign';

const ALGORITHM_NAME = 'AES-GCM';
const ALGORITHM_LENGTH = 256;

const genEncryptionKey = async (password, keyAlgorithm) => {
  const algorithmName = 'PBKDF2';
  const algorithm = {
    name: algorithmName,
    hash: 'SHA-256',
    salt: new TextEncoder().encode(''),
    iterations: 1000,
  };
  const derived = { name: keyAlgorithm.name, length: keyAlgorithm.length };
  const encoded = new TextEncoder().encode(password);
  const key = await crypto.subtle.importKey('raw', encoded, { name: algorithmName }, false, ['deriveKey']);

  return crypto.subtle.deriveKey(algorithm, key, derived, false, ['encrypt', 'decrypt']);
};

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

class JWK { // eslint-disable-line fp/no-class
  generate = () => {
    const keyPair = KEYUTIL.generateKeypair('EC', 'secp256r1');
    const kid = uuid.v4();
    return {
      privateKey: {
        ...KEYUTIL.getJWKFromKey(keyPair.prvKeyObj),
        kid,
      },
      publicKey: {
        ...KEYUTIL.getJWKFromKey(keyPair.pubKeyObj),
        kid,
      },
    };
  };

  encrypt = async (key, password) => {
    const algorithm = {
      name: ALGORITHM_NAME,
      length: ALGORITHM_LENGTH,
      iv: crypto.getRandomValues(new Uint8Array(12)),
    };
    const encryptionKey = await genEncryptionKey(password, algorithm);
    const encoded = new TextEncoder().encode(JSON.stringify(key));
    const cipherBuf = await crypto.subtle.encrypt(algorithm, encryptionKey, encoded);

    return {
      cipherText: arrayBufferToBase64(cipherBuf),
      iv: Array.from(algorithm.iv),
    };
  };

  decrypt = async (encrypted, password) => {
    const algorithm = {
      name: ALGORITHM_NAME,
      length: ALGORITHM_LENGTH,
      iv: new Uint8Array(encrypted.iv),
    };
    const cipherBuf = base64ToArrayBuffer(encrypted.cipherText);
    const key = await genEncryptionKey(password, algorithm);
    const decrypted = await crypto.subtle.decrypt(algorithm, key, cipherBuf);
    const decryptedKey = await new TextDecoder().decode(decrypted);

    return JSON.parse(decryptedKey);
  };

  sign = (payload, key) => {
    const currentKey = KEYUTIL.getKey(key);
    const header = { alg: 'ES256', kid: key.kid };
    return KJUR.jws.JWS.sign(header.alg, header, payload, currentKey);
  };
}

export default new JWK();
